import React, { useState, useEffect } from "react";
// Added Download icon
import { ArrowLeft, Upload, Check, AlertCircle, Download } from "lucide-react";
import { Button } from "components/ui/button";
import {
  previewCrewCsv,
  bulkSaveCrew,
  getCrewRoles,
  getSkills,
} from "services/crewService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import MultiSelect from "components/ui/multiSelect";

const BulkUploadCrew = ({ onBack, onComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [skillsMaster, setSkillsMaster] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- NEW: Function to download your provided crew file ---
  const handleDownloadTemplate = () => {
    const templatePath = "/templates/crew_template.csv";
    const link = document.createElement("a");
    link.href = templatePath;
    link.setAttribute("download", "crew_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, skillsRes] = await Promise.all([
          getCrewRoles(),
          getSkills(),
        ]);

        const rolesArray =
          rolesRes?.data?.data ||
          rolesRes?.data ||
          (Array.isArray(rolesRes) ? rolesRes : []);
        const skillsArray =
          skillsRes?.data?.data ||
          skillsRes?.data ||
          (Array.isArray(skillsRes) ? skillsRes : []);

        setRoles(rolesArray);
        setSkillsMaster(skillsArray);
      } catch (err) {
        console.error("Failed to fetch initial data", err);
      }
    };
    fetchData();
  }, []);

  const handleRowUpdate = (index, field, value) => {
    const updatedData = [...previewData];
    updatedData[index][field] = value;
    setPreviewData(updatedData);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
  };

  const handleUploadPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await previewCrewCsv(formData);
      const responseBody = res.data || res;

      let dataToMap = null;
      if (Array.isArray(responseBody)) {
        dataToMap = responseBody;
      } else if (responseBody && Array.isArray(responseBody.data)) {
        dataToMap = responseBody.data;
      }

      if (dataToMap) {
        const initialData = dataToMap.map((item) => ({
          ...item,
          primary_role: item.primary_role ? String(item.primary_role) : "",
          skills: Array.isArray(item.skills) ? item.skills : [],
        }));
        setPreviewData(initialData);
        setStep(2);
      } else {
        setError(responseBody?.message || "No data found in CSV response.");
      }
    } catch (err) {
      setError("Error uploading file.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSave = async () => {
    setLoading(true);
    try {
      const res = await bulkSaveCrew({ crew_list: previewData });
      const resData = res.data || res;
      if (!resData.error) {
        onComplete();
      } else {
        setError(resData.message || "Failed to save crew.");
      }
    } catch (err) {
      setError("Server error during save.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 w-full max-w-screen-xl mx-auto overflow-hidden">
      {/* Updated Header with Download Button */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} size="icon">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Bulk Upload Crew</h1>
            <p className="text-muted-foreground text-sm">
              Import multiple crew members via CSV
            </p>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="hidden md:flex items-center gap-2 border-primary text-primary hover:bg-primary/5"
        >
          <Download size={16} />
          Download Template
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {step === 1 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-12 text-center">
          <Upload className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">Select Crew CSV</h2>

          {/* New informative text with download link */}
          <p className="text-sm text-muted-foreground mb-6">
            Please use our official template to ensure data compatibility.<br/>
            <button
              onClick={handleDownloadTemplate}
              className="text-primary font-medium hover:underline inline-flex items-center gap-1 mt-1"
            >
              Download crew_template.csv
            </button>
          </p>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="crew-csv"
          />
          <label htmlFor="crew-csv">
            <Button asChild variant="outline" className="cursor-pointer">
              <span>{file ? file.name : "Choose File"}</span>
            </Button>
          </label>
          <div className="mt-8">
            <Button
              className="bg-button-coloured px-8"
              disabled={!file || loading}
              onClick={handleUploadPreview}
            >
              {loading ? "Parsing..." : "Next: Map Roles & Skills"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col overflow-hidden w-full">
          <div className="w-full overflow-x-auto no-scrollbar scrollbar-thin scrollbar-thumb-gray-300">
            <table className="min-w-full text-sm text-left border-collapse table-auto">
              <thead className="bg-muted/50 text-muted-foreground font-medium border-b">
                <tr>
                  <th className="px-6 py-4 whitespace-nowrap">Name</th>
                  <th className="px-6 py-4 whitespace-nowrap">Email</th>
                  <th className="px-6 py-4 whitespace-nowrap">Phone</th>
                  <th className="px-6 py-4 whitespace-nowrap">Location</th>
                  <th className="px-6 py-4 whitespace-nowrap">Distance</th>
                  <th className="px-6 py-4 text-center whitespace-nowrap">Exp</th>
                  <th className="px-6 py-4 whitespace-nowrap">Rate</th>
                  <th className="px-6 py-4 whitespace-nowrap">Primary Role</th>
                  <th className="px-6 py-4 whitespace-nowrap">Skills</th>
                  <th className="px-6 py-4 whitespace-nowrap">Bio</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-border">
                {previewData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3 font-semibold whitespace-nowrap">
                      {item.first_name} {item.last_name}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">
                      {item.email}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      {item.phone_number}
                    </td>
                    <td className="px-6 py-3 text-xs whitespace-nowrap">
                      {item.location}
                    </td>
                    <td className="px-6 py-3 text-xs whitespace-nowrap">
                      {item.working_distance}
                    </td>
                    <td className="px-6 py-3 text-center whitespace-nowrap">
                      {item.years_of_experience}y
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap font-medium">
                      ${item.hourly_rate}/hr
                    </td>

                    <td className="px-6 py-3 min-w-[200px]">
                      <Select
                        value={String(item.primary_role) || ""}
                        onValueChange={(val) => handleRowUpdate(idx, "primary_role", val)}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Select Role" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((r) => (
                            <SelectItem key={r.role_id} value={String(r.role_id)}>
                              {r.role_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    <td className="px-6 py-3 min-w-[200px]">
                      <MultiSelect
                        placeholder="Select Skills"
                        options={skillsMaster}
                        value={item.skills.map(String)}
                        onChange={(vals) => handleRowUpdate(idx, "skills", vals)}
                      />
                    </td>

                    <td className="px-6 py-3 min-w-[250px]">
                      <p className="text-xs text-muted-foreground line-clamp-1" title={item.bio}>
                        {item.bio === "NULL" || !item.bio ? "—" : item.bio}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex justify-between items-center bg-muted/10">
            <Button variant="outline" onClick={() => setStep(1)}>
              Change File
            </Button>
            <div className="flex items-center gap-4 md:gap-6">
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Total Crew: <span className="text-foreground">{previewData.length}</span>
              </span>
              <Button
                className="bg-black text-white hover:bg-black/90 px-6 whitespace-nowrap"
                onClick={handleFinalSave}
                disabled={loading}
              >
                {loading ? "Saving..." : <><Check className="mr-2 h-4 w-4" /> Save Crew Members</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadCrew;
